<template>
  <b-card
    no-body
    tag="article"
    class="mb-3 mr-3"
    :class="(!target.id) ? 'newTarget' : ''"
    :header-bg-variant="(!target.id) ? 'success' : ''"
  >
    <h4 slot="header" class="mb-0">
      <i class="fas" :class="headerIcon"></i>
      <span class="pl-2">{{displayMethod}}</span>
      <div class="float-right" v-if="editable && target.id">
        <label class="switch switch-sm switch-text switch-info mb-0">
          <input class="switch-input" type="checkbox" v-model="target.enabled" @click.stop="target.enabled = !target.enabled; saveTarget();">
          <span data-on="On" data-off="Off" class="switch-label"></span>
          <span class="switch-handle"></span>
        </label>
      </div>
    </h4>
    <b-card-body>
      <p class="card-text">
        <b-form>
          <b-form-group
            label="Type:"
            label-for="targetType"
          >
            <b-form-select
              id="targetType"
              v-model="target.method"
              :options="methodOptions"
              required
            />
          </b-form-group>
          <b-form-group
            label="Label:"
            label-for="targetLabel"
            description="Just a label to identify this - e.g., 'Personal Email'"
          >
            <b-form-input
              id="targetLabel"
              v-model="target.label"
              type="text"
              required
            />
          </b-form-group>
          <b-form-group
            label="Destination:"
            label-for="targetDestination"
            description="Where the alert should be sent - e.g., 'me@me.com'"
          >
          <b-form-input
            id="targetDestination"
            v-model="target.destination"
            type="text"
            required
            placeholder=""
          />
        </b-form-group>
        <b-button @click.stop="saveTarget()" size="sm" variant="success" class="mr-1"><i class="fas fa-check"></i><span class="pl-2">Save</span></b-button>
        <b-button @click.stop="deleteTarget()" v-if="target.id" size="sm" variant="danger" class="mr-1"><i class="fas fa-trash"></i><span class="pl-2">Delete</span></b-button>
        <b-button @click.stop="sendTest()" v-if="target.id && (target.method === 'SMS' || target.method === 'EMAIL')" size="sm" variant="outline-info" class="mr-1" :disabled="testing"><i class="fas fa-paper-plane"></i><span class="pl-2">{{ testing ? 'Sending...' : 'Test' }}</span></b-button>
      </b-form>
      </p>
    </b-card-body>
  </b-card>
</template>

<script>
import { CREATE_TARGET_MUTATION, UPDATE_TARGET_MUTATION, DELETE_TARGET_MUTATION, TEST_ALERT_DELIVERY_MUTATION } from '@/constants/graphql'

export default {
  name: 'TargetCard',
  props: {
    target: {
      type: Object,
      default () {
        return {
          id: null,
          enabled: true,
          label: null,
          method: null,
          destination: null
        }
      }
    }
  },
  data () {
    return {
      loading: 0,
      error: null,
      testing: false,
      methodOptions: [
        { value: null, text: 'Please select a method' },
        { value: 'EMAIL', text: 'Email' },
        { value: 'SMS', text: 'Text Message' }
      ]
    }
  },
  computed: {
    editable () {
      return this.target.method !== 'default'
    },
    displayMethod () {
      if (!this.target.id) return 'Add New Target'

      switch (this.target.method) {
        case 'SMS':
          return 'Text Message';
        case 'EMAIL':
          return 'Email';
        default:
          return 'Dashboard'
      }
    },
    headerIcon () {
      if (!this.target.id) return 'fa-plus'

      switch (this.target.method) {
        case 'SMS':
          return 'fa-mobile-alt';
        case 'EMAIL':
          return 'fa-envelope';
        default:
          return 'fa-tachometer-alt'
      }
    }
  },
  methods: {
    saveTarget () {
      let data = {...this.target}
      delete data.id

      if (this.target.id) {
        // update
        this.$apollo.mutate({
          mutation: UPDATE_TARGET_MUTATION,
          variables: { data, id: this.target.id }
        }).then(() => {
          this.loading = false
          this.$swal.fire({ icon: 'success', title: 'Success! Notification Target Updated' })

        }).catch((error) => {
          this.loading = false
          this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
        })
      } else {
        // new target
        if (
          !this.target.label ||
          !this.target.method ||
          !this.target.destination
        ) {
          this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: Please make sure all fields are filled in before saving new notification target.' })
          return
        }

        this.$apollo.mutate({
          mutation: CREATE_TARGET_MUTATION,
          variables: { data }
        }).then(() => {
          this.loading = false
          this.$swal.fire({ icon: 'success', title: 'Success! Notification Target Created' })
          this.$emit('target-saved')
        }).catch((error) => {
          this.loading = false
          this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
        })
      }
    },
    sendTest () {
      this.testing = true
      this.$apollo.mutate({
        mutation: TEST_ALERT_DELIVERY_MUTATION,
        variables: { targetId: this.target.id }
      }).then((result) => {
        const { success, message } = result.data.testAlertDelivery
        this.$swal.fire({
          icon: success ? 'success' : 'error',
          title: success ? 'Test Sent!' : 'Test Failed',
          text: message,
          timer: 0,
          showConfirmButton: true
        })
      }).catch((error) => {
        this.$swal.fire({ icon: 'error', timer: 0, showConfirmButton: true, title: 'Test Failed', text: error.message })
      }).finally(() => {
        this.testing = false
      })
    },
    deleteTarget () {
      this.$swal.fire({
        title: `Delete this target?`,
        icon: 'warning',
        toast: false,
        position: 'top',
        showConfirmButton: true,
        timer: 0,
        timerProgressBar: false,
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: `Yes, delete it!`
      }).then((result) => {
        if (result.isConfirmed && this.target.id) {
          this.$apollo.mutate({
            mutation: DELETE_TARGET_MUTATION,
            variables: { id: this.target.id }
          }).then(() => {
            this.loading = false
            this.$swal.fire({ icon: 'success', title: 'Success! Notification Target Deleted' })
            this.$emit('target-deleted')
          }).catch((error) => {
            this.loading = false
            this.$swal.fire({ icon: 'error', timer: 3500, title: 'Error: ' + error })
          })
        }
      })
    }
  }
}
</script>

<style>
.card-header h4 {
    font-size: 1.2em;
    line-height: 1.2em;
}
article.card {
  width: 22rem;
  min-width: 20rem;
  max-width: 25rem;
}
article.card {
  background-color: rgba(255,255,255, 0.9);
}
</style>
